// src/InvoiceViewPage.jsx

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "./firebase";
import {
  doc,
  getDoc,
  collection,
  getDocs,
  query,
  orderBy,
  updateDoc,
  setDoc,
  deleteField,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { apiFetch, API_BASE } from "./utils/apiClient";
import { getProjectInvoiceLabel } from "./utils/projectCalc";
import { hasPermission } from "./utils/userProfile";

const InvoiceViewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [invoice, setInvoice] = useState(null);
  const [versions, setVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [approveLoading, setApproveLoading] = useState(false);
  const [error, setError] = useState("");

  const username = localStorage.getItem("username") || "";
  // + أي حساب ممنوح بوكس "الفواتير" أو "انشاء فاتورة" (مثل تيهان) - نفس صلاحية
  // الاعتماد بالسيرفر (requireInvoiceAccess بـinvoiceRoutes.js)
  const canEdit =
    ["rand@123", "jumana@123", "sara@123", "Baneen@123", "admin", "farah@123", "karena@123","maysa@123"].includes(username) ||
    hasPermission("invoices") ||
    hasPermission("createInvoice");
  const isAdmin = username === "admin";
const ceilToStep = (value, step) => Math.ceil(value / step) * step;

const getInvoiceItemDimensions = (item) => {
  if (item.sizeSource === "custom") {
    return {
      height: Number(item.customHeight || 0),
      width: Number(item.customWidth || 0),
    };
  }

  if (item.sizeKey) {
    const [w, h] = item.sizeKey.split("x").map(Number);

    return {
      width: Number(w || 0),
      height: Number(h || 0),
    };
  }

  return {
    width: 0,
    height: 0,
  };
};

const calculateInvoiceProductionCosts = (invoiceData) => {
  return (invoiceData.items || []).reduce(
    (totals, item) => {
      const { width, height } = getInvoiceItemDimensions(item);
      const quantity = Number(item.quantity || 0);

      if (width <= 0 || height <= 0 || quantity <= 0) {
        return totals;
      }

      // نفس معادلة واجهة حساب تكلفة اللوحة
      const printCost =
        ceilToStep((height / 100 + 0.05) * (width / 100 + 0.05), 0.2) *
        25000;

      const baseDim = Math.ceil((height / 100 + width / 100) * 2 + 0.5);

      const innerWood = baseDim * 2000;
      const ailFrame = baseDim * 5500;
      const kasFrame = baseDim * 7500;

      let frameCost = 0;

      if (item.frameOption === "إطار داخلي فقط") {
        frameCost = innerWood;
      }

      if (item.frameOption === "إطار كامل") {
        if (item.frameType === "L") {
          frameCost = innerWood + ailFrame;
        } else if (item.frameType === "Kas") {
          frameCost = innerWood + kasFrame;
        } else {
          frameCost = innerWood;
        }
      }

      return {
        printTotal: totals.printTotal + printCost * quantity,
        frameTotal: totals.frameTotal + frameCost * quantity,
      };
    },
    {
      printTotal: 0,
      frameTotal: 0,
    }
  );
};

const createInvoiceCostAccounts = async ({ invoiceData, invoiceIdValue, version }) => {
  const { printTotal, frameTotal } = calculateInvoiceProductionCosts(invoiceData);

  const invoiceDate = invoiceData.date
    ? new Date(invoiceData.date)
    : new Date();

  const invoiceImageUrl =
    version.imageUrl ||
    version.data?.imageUrl ||
    "";

  const basePayload = {
    isInvoiceCostAccount: true,
    source: "approved_invoice",

    // مهم: حتى لا تدخل بالصناديق ولا الجدول الرئيسي ولا الإكسل العام
    hiddenFromTable: true,
    hiddenFromExcel: true,
    balanceOnly: true,

    affectsWallet: false,
    affectsExpense: false,
    isInternalTransfer: false,

    type: "تكلفة فاتورة",
    accountingCategory: "",

    wallet: null,
    transferTo: null,
    location: "",

    processName: invoiceData.customerName || "-",
    customerName: invoiceData.customerName || "-",

    invoiceId: invoiceIdValue,
    versionId: version.id,
    versionNumber: version.versionNumber || null,

    invoiceNumber: invoiceData.invoiceNumber || "",
    receiptNumber: invoiceData.invoiceNumber || "بدون",

    invoiceUrl: invoiceImageUrl,
    invoiceUrls: invoiceImageUrl ? [invoiceImageUrl] : [],

    date: Timestamp.fromDate(invoiceDate),
    timestamp: Timestamp.now(),

    createdBy: username,
    createdByUsername: username,
  };

  // ✅ قيد المطبعة
  await setDoc(
    doc(db, "accounting_pro", `invoice_cost_print_${invoiceIdValue}`),
    {
      ...basePayload,
      invoiceCostAccountName: "مطبعة صدى سومر 2",
      amountIQD: Math.round(printTotal || 0),
    },
    { merge: true }
  );

  // ✅ قيد الإطارات
  await setDoc(
    doc(db, "accounting_pro", `invoice_cost_frame_${invoiceIdValue}`),
    {
      ...basePayload,
      invoiceCostAccountName: "إطارات توب فريم 2",
      amountIQD: Math.round(frameTotal || 0),
    },
    { merge: true }
  );
};
  // 🔥 تنسيق التاريخ
  const formatDate = (ts) => {
    if (!ts) return "—";
    try {
      return ts.toDate().toLocaleString("ar-IQ", {
        dateStyle: "short",
        timeStyle: "short",
      });
    } catch {
      return "—";
    }
  };

  // 🔥 إصلاح النسخ القديمة داخل الفايربيس (مهم جداً)
 const fixOldInvoiceStructure = async () => {
  const invoiceRef = doc(db, "invoices", id);
  const snap = await getDoc(invoiceRef);
  if (!snap.exists()) return;

  const data = snap.data();

  // ✅ إذا النسخ كانت Array داخل الحقل versions (مثل لقطتك)
  if (Array.isArray(data.versions) && data.versions.length > 0) {
    console.log("⚠️ نقل versions (Array) إلى Subcollection...");

    for (let i = 0; i < data.versions.length; i++) {
      const ver = data.versions[i];
      const vNum = ver.versionNumber ?? i;

      // نخزنها كـ doc جديد
      await setDoc(doc(db, "invoices", id, "versions", `legacy_${vNum}_${i}`), {
        versionNumber: vNum,
        createdAt: ver.createdAt || serverTimestamp(),
        createdBy: ver.createdBy || data.creatorName || data.createdBy || "—",
        updatedBy: ver.updatedBy || ver.createdByUsername || data.creatorUsername || "—",
        data: ver.data || {},
        imageUrl: ver.imageUrl || ver.data?.imageUrl || "",
        finalTotal: ver.data?.finalTotal ?? ver.finalTotal ?? 0,
        approved: ver.approved ?? false,
      });
    }

    // حذف الحقل القديم
    await updateDoc(invoiceRef, { versions: deleteField() });

    console.log("✅ تم النقل بنجاح");
  }

  // ✅ إذا كانت Object (قديم جداً) خليه مثل ما عندك (موجود بكودك)
  if (data.versions && typeof data.versions === "object" && !Array.isArray(data.versions)) {
    console.log("⚠️ إصلاح versions كـ Object...");
    const versionsObj = data.versions;

    for (const key in versionsObj) {
      const verData = versionsObj[key];
      await setDoc(doc(db, "invoices", id, "versions", key), verData);
    }

    await updateDoc(invoiceRef, { versions: deleteField() });
    console.log("✅ تم الإصلاح بنجاح!");
  }
};

  // 🔥 تحميل الفاتورة + النسخ
  const loadData = async () => {
    try {
      setLoading(true);
      setError("");

      const invRef = doc(db, "invoices", id);
      const invSnap = await getDoc(invRef);

      if (!invSnap.exists()) {
        setError("❌ الفاتورة غير موجودة.");
        setLoading(false);
        return;
      }

      const invData = { id: invSnap.id, ...invSnap.data() };
      setInvoice(invData);

      const verRef = collection(db, "invoices", id, "versions");
      const q = query(verRef, orderBy("versionNumber", "desc"));
      const verSnap = await getDocs(q);

    const list = verSnap.docs.map((d) => {
  const v = d.data();
  const realFinal = v.finalTotal ?? v.data?.finalTotal ?? 0;
  const realImg = v.imageUrl ?? v.data?.imageUrl ?? "";

  return {
    id: d.id,
    ...v,
    finalTotal: realFinal,
    imageUrl: realImg,
  };
});

      setVersions(list);
      setSelectedVersion(list.length > 0 ? list[0] : null);

    } catch (err) {
      console.error("Error loading invoice:", err);
      setError("حدث خطأ أثناء تحميل الفاتورة.");
    } finally {
      setLoading(false);
    }
  };

  // أول شيء: أصلح الهيكل… بعدها حمّل البيانات
  useEffect(() => {
    const init = async () => {
      await fixOldInvoiceStructure();
      await loadData();
    };
    init();
  }, [id]);

  // 🔥 اعتماد النسخة
 const handleApprove = async (version) => {
  if (!invoice || !version) return;

  if (!canEdit) {
    alert("لا تملك صلاحية اعتماد الفاتورة.");
    return;
  }

  // ✅ Phase 07-D: فحص !=null بدل truthy - يقبل approvedVersionNumber=0 (أول
  // نسخة) كـ"معتمدة فعلاً" بدل تجاهله بصمت والسماح بمحاولة إعادة اعتماد
  // (الرفض النهائي والموثوق يبقى سيرفرياً بـapproveInvoice's isInvoiceApproved،
  // هذا فقط تحسين لتجربة الواجهة).
  if (invoice.approvedVersionNumber !== undefined && invoice.approvedVersionNumber !== null) {
    alert("🔒 هذه الفاتورة معتمدة بالفعل ولا يمكن إعادة اعتمادها.");
    return;
  }

  const ok = window.confirm(`هل أنت متأكد من اعتماد النسخة V${version.versionNumber}؟`);
  if (!ok) return;

  try {
    setApproveLoading(true);

    // ✅ Phase 07-C2a: يستبدل الكتابات المباشرة (اعتماد الفاتورة + النسخة +
    // قيدا كلفة الطباعة/الإطارات) بنداء واحد للباك اند - نفس الـendpoint
    // المستخدَم من InvoicePage.jsx's handleSaveInvoice، ينهي الازدواج بين
    // الملفين ويجعل الاعتماد ذرياً (batch واحد بدل 3 كتابات منفصلة).
    const approveRes = await apiFetch(`${API_BASE}/invoices/${invoice.id}/approve`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ versionId: version.id }),
    });
    if (!approveRes.ok) {
      throw new Error(await approveRes.text().catch(() => ""));
    }

    alert("✅ تم اعتماد النسخة بنجاح وتم ترحيل كلفة الطباعة والإطارات.");

   alert("✅ تم اعتماد النسخة بنجاح.");

// 👇 جلب بيانات الفاتورة (نسخة الفاتورة المعتمدة الفعلية)
const data = version.data || {};

// 👇 حساب عدد اللوحات
const totalPanels = data.items?.reduce(
  (sum, item) => sum + Number(item.quantity || 0),
  0
);

// 👇 الانتقال إلى AddOrder مع تعبئة البيانات
navigate("/add", {
  state: {
    fromInvoice: true,
    customerName: data.customerName || invoice.customerName,
    governorate: data.governorate || "",
    price: data.finalTotal ?? invoice.lastFinalTotal ?? 0,
    note: `فاتورة رقم ${invoice.invoiceNumber}`,
    boardsCount: totalPanels || 0,
    invoiceData: invoice,
    // 🧾 صورة الفاتورة المعتمدة - تروح مع الطلب لتظهر بواجهة برهان
    invoiceImageUrl: version.imageUrl || version.data?.imageUrl || "",
    // 📄 ملف PDF كامل (صورة الفاتورة + صفحة تفاصيل وصورة لكل لوحة) - يظهر بواجهة برهان بدل الصورة
    invoicePdfUrl: version.pdfUrl || version.data?.pdfUrl || "",
    // 💳 معلومات الدفع من الفاتورة (تعبئة تلقائية بواجهة إضافة الطلب)
    invoicePaymentMethod: data.paymentMethod || "cod",
    invoiceAdvancePayment: data.advancePayment ?? 0,
    // 🧮 حساب البيع - نفس المختار بالفاتورة (تعبئة تلقائية بواجهة إضافة الطلب)
    saleAccount: data.saleAccount || invoice.saleAccount || "بيع",
    // 🤝 فاتورة ارت تاتش - الطلب ينضاف بمصدر "ارت تاتش" + رقم الزبون
    partnerSource: invoice.partnerSource || "",
    customerPhone: data.phone || invoice.customerPhone || "",
    // 🤝 خصم الشريك (ست سارة) - المصدر يتعبّى تلقائياً بالطلب
    partnerDiscountSource: data.partnerDiscountSource || "",
    partnerDiscountPercent: data.partnerDiscountPercent || 0,
    partnerDiscountAmount: data.partnerDiscountAmount || 0,
  },
});
  } catch (err) {
    console.error("Error approving:", err);
    alert("حدث خطأ أثناء الاعتماد.");
  } finally {
    setApproveLoading(false);
  }
};

  // 🔓 إلغاء اعتماد الفاتورة - المدير فقط. كان يتم يدوياً مباشرة بالداتابيس،
  // هنا نفس المنطق عبر endpoint محمي (requireAdmin بالباك اند).
  // 🔎 يجيب حالة الطلب المرتبط بالفاتورة (إن وجد) قبل التأكيد - نفس المنطق
  // المستخدم بـInvoiceListPage.jsx (history.length > 1 يعني تقدّم فعلي
  // بمراحل العمل بعد الإنشاء، نفس فحص unapproveInvoice سيرفرياً)
  const fetchLinkedOrderInfo = async (orderId) => {
    if (!orderId) return null;
    const orderSnap = await getDoc(doc(db, "orders", orderId));
    if (!orderSnap.exists()) return null;
    const data = orderSnap.data();
    return {
      status: data.status || "—",
      inProgress: Array.isArray(data.history) && data.history.length > 1,
    };
  };

  const describeOrderImpact = (orderInfo) => {
    if (!orderInfo) {
      return "لا يوجد طلب مرتبط حالياً بهذه الفاتورة.";
    }
    if (orderInfo.inProgress) {
      return `⚠️ تنبيه: هذه الفاتورة مرتبطة بطلب قيد التنفيذ فعلياً (الحالة الحالية: "${orderInfo.status}"). أي عمل أُنجز عليه سيُفقد نهائياً - سيتم حذفه بالكامل مع أي دفعة مقدمة مسجّلة عليه.`;
    }
    return `هذه الفاتورة مرتبطة بطلب لم يبدأ العمل عليه بعد (الحالة الحالية: "${orderInfo.status}"). سيتم حذفه مع أي دفعة مقدمة مسجّلة عليه (إن وجدت).`;
  };

  const handleUnapprove = async () => {
    if (!invoice) return;

    if (!isAdmin) {
      alert("لا تملك صلاحية إلغاء اعتماد الفاتورة.");
      return;
    }

    let orderInfo = null;
    if (invoice.orderId) {
      try {
        orderInfo = await fetchLinkedOrderInfo(invoice.orderId);
      } catch (err) {
        console.error("Error checking linked order:", err);
      }
    }

    const ok = window.confirm(
      `هل أنت متأكد من إلغاء اعتماد هذه الفاتورة؟\n\n${describeOrderImpact(
        orderInfo
      )}\n\nقيود كلفة الطباعة والإطارات المرتبطة بهذا الاعتماد تنحذف من الحسابات، وتعود الفاتورة "غير معتمدة" وقابلة للتعديل.`
    );
    if (!ok) return;

    try {
      setApproveLoading(true);

      const res = await apiFetch(`${API_BASE}/invoices/${invoice.id}/unapprove`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      if (!res.ok) {
        throw new Error(await res.text().catch(() => ""));
      }
      const result = await res.json().catch(() => ({}));

      if (result.orderDeleted) {
        alert(
          result.orderWasInProgress
            ? "✅ تم إلغاء اعتماد الفاتورة، وتم حذف الطلب المرتبط بها (كان قيد التنفيذ) مع أي دفعة مقدمة مسجّلة عليه."
            : "✅ تم إلغاء اعتماد الفاتورة، وتم حذف الطلب المرتبط بها (لم يكن قد بدأ العمل عليه) مع أي دفعة مقدمة مسجّلة عليه."
        );
      } else {
        alert("✅ تم إلغاء اعتماد الفاتورة.");
      }
      await loadData();
    } catch (err) {
      console.error("Error unapproving:", err);
      alert("حدث خطأ أثناء إلغاء الاعتماد.");
    } finally {
      setApproveLoading(false);
    }
  };

  // ========================= UI ==============================

  if (loading) {
    return (
      <div style={pageLoading}>
        <p style={{ fontSize: 18 }}>⏳ جاري تحميل الفاتورة...</p>
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div style={pageLoading}>
        <p style={{ color: "red", marginBottom: 20 }}>{error}</p>

        <button onClick={() => navigate("/invoices")} style={backBtn}>
          ⬅ الرجوع لقائمة الفواتير
        </button>
      </div>
    );
  }

  // ✅ Phase 07-D: ?? بدل || - يحافظ على approvedVersionNumber=0 (أول نسخة)
  // بدل تحويله null بالخطأ (كان || يحوّل أي falsy، بما فيها 0، إلى null)
  const approvedVersionNumber = invoice.approvedVersionNumber ?? null;
  // 🔒 بمجرد اعتماد أي نسخة، تصير كل النسخ (المعتمدة والسابقة) مقفلة تماماً
  // عن التعديل أو إعادة الاعتماد - يحمي تطابق كلفة الفواتير بسستم الحسابات.
  // !== null بدل !! - يقبل approvedVersionNumber=0 كـ"معتمدة فعلاً" (Phase 07-D)
  const invoiceLocked = approvedVersionNumber !== null;

  return (
    <div style={{ padding: 20 }}>
      {/* ================= الشريط العلوي ================= */}
      <div style={headerRow}>
        <button onClick={() => navigate("/invoices")} style={grayBtn}>
          ⬅ الرجوع لقائمة الفواتير
        </button>

        <h2 style={{ margin: 0 }}>
          🧾 عرض الفاتورة رقم {invoice.invoiceNumber}
          {invoice.invoiceSource === "projectQuote" && (
            <span style={{ marginRight: 10, fontSize: 13, fontWeight: 700, padding: "4px 10px", borderRadius: 20, background: "#fff8e1", color: "#7a5200" }}>
              📋 تسعيرة
            </span>
          )}
          {(invoice.invoiceSource === "projectCost" ||
            invoice.invoiceSource === "projectInvoice") && (
            <span
              style={{
                marginRight: 10,
                fontSize: 13,
                fontWeight: 700,
                padding: "4px 10px",
                borderRadius: 20,
                background: "#ede7f6",
                color: "#4527a0",
                verticalAlign: "middle",
              }}
            >
              {getProjectInvoiceLabel(invoice.createdBy)}
            </span>
          )}
        </h2>

        <div style={{ width: 120 }} />
      </div>

      {/* ================= المحتوى الرئيسي ================= */}
      <div style={mainGrid}>
        {/* يسار: معاينة النسخة */}
        <div style={leftBox}>
          <h3 style={{ marginBottom: 12, textAlign: "right" }}>
            👁 معاينة النسخة المختارة{" "}
            {selectedVersion ? `(V${selectedVersion.versionNumber})` : ""}
          </h3>

          {canEdit && selectedVersion && !invoiceLocked && (
            <button
              onClick={() =>
                navigate(`/invoice/version/edit/${invoice.id}/${selectedVersion.id}`)
              }
              style={editVersionBtn}
            >
              ✏ تعديل النسخة
            </button>
          )}

          {invoiceLocked && (
            <p style={{ color: "#c62828", fontSize: 13, marginBottom: 8 }}>
              🔒 الفاتورة معتمدة - كل النسخ مقفلة عن التعديل أو إعادة الاعتماد.
            </p>
          )}

          {invoiceLocked && isAdmin && (
            <button
              onClick={handleUnapprove}
              disabled={approveLoading}
              style={unapproveBtn}
            >
              🔓 إلغاء اعتماد الفاتورة
            </button>
          )}

          {selectedVersion?.imageUrl ? (
            <img src={selectedVersion.imageUrl} style={previewImg} />
          ) : (
            <div style={emptyPreview}>لا توجد صورة محفوظة لهذه النسخة.</div>
          )}
        </div>

        {/* يمين: قائمة النسخ */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={infoBox}>
            <h3 style={{ textAlign: "right" }}>📌 معلومات عامة</h3>

            <p style={infoRow}>👤 الزبون: {invoice.customerName}</p>
            <p style={infoRow}>🧾 رقم الفاتورة: {invoice.invoiceNumber}</p>
            <p style={infoRow}>👩‍💼 تم الإنشاء بواسطة: {invoice.createdBy}</p>

            <p style={infoRow}>🔢 آخر نسخة: V{invoice.lastVersionNumber || 1}</p>

            <p style={infoRow}>
              ✅ النسخة المعتمدة:{" "}
              {/* Phase 07-D: !== null بدل truthy - V0 كانت تُعرَض خطأً كـ"لم تُعتمد بعد" */}
              {approvedVersionNumber !== null ? `V${approvedVersionNumber}` : "لم تُعتمد بعد"}
            </p>

            <p style={{ ...infoRow, fontWeight: "600" }}>
              💰 آخر مجموع نهائي:{" "}
              {invoice.lastFinalTotal ? invoice.lastFinalTotal.toLocaleString() : "—"} د.ع
            </p>
          </div>

          <div style={versionsBox}>
            <h3 style={{ marginBottom: 10, textAlign: "right" }}>📚 النسخ السابقة</h3>

            {versions.length === 0 ? (
<p style={{ textAlign: "center", color: "#777", marginTop: 20 }}>
  لا توجد نسخ محفوظة بعد لهذه الفاتورة.
</p>
            ) : (
              versions.map((ver) => {
                const selected = selectedVersion?.id === ver.id;
                const approved = approvedVersionNumber === ver.versionNumber;

                return (
                  <div
                    key={ver.id}
                    style={{
                      ...versionCard,
                      background: selected ? "#e7f1ff" : "#fafafa",
                    }}
                  >
                    <strong>
                      V{ver.versionNumber} —{" "}
                      {ver.finalTotal ? ver.finalTotal.toLocaleString() + " د.ع" : "—"}
                    </strong>

                    {approved && (
                      <span style={{ color: "green" }}>✔ النسخة المعتمدة</span>
                    )}

                    <p style={smallRow}>🧑‍🎨 تم التعديل بواسطة: {ver.updatedBy || "—"}</p>
                    <p style={smallRow}>📅 {formatDate(ver.createdAt)}</p>

                    <div style={versionBtns}>
                      <button
                        onClick={() => setSelectedVersion(ver)}
                        style={viewBtn}
                      >
                        👁 عرض
                      </button>

                      {ver.pdfUrl && (
                        <button
                          onClick={() => window.open(ver.pdfUrl, "_blank")}
                          style={pdfBtn}
                        >
                          📄 PDF كامل
                        </button>
                      )}

                      {/* 📋 التسعيرة ما تنعتمد من هنا - اعتمادها من عروض الأسعار
                          يحوّلها لفاتورة مشروع (بدون قيود كلفة ولا طلب) */}
                      {canEdit && !invoiceLocked && invoice.invoiceSource !== "projectQuote" && (
                        <button
                          onClick={() => handleApprove(ver)}
                          style={approveBtn}
                        >
                          ✔ اعتماد
                        </button>
                      )}
                      {canEdit && invoice.invoiceSource === "projectQuote" && (
                        <span style={{ fontSize: 12, color: "#7a5200" }}>
                          📋 تسعيرة - الاعتماد من عروض الأسعار
                        </span>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ====================== STYLES ====================== */

const pageLoading = {
  width: "100%",
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const headerRow = {
  display: "flex",
  justifyContent: "space-between",
  marginBottom: 20,
  alignItems: "center",
};

const mainGrid = {
  display: "grid",
  gridTemplateColumns: "minmax(0,2fr) minmax(0,1.4fr)",
  gap: 20,
};

const leftBox = {
  background: "#fff",
  borderRadius: 12,
  padding: 16,
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
};

const previewImg = {
  width: "100%",
  maxHeight: "80vh",
  objectFit: "contain",
  borderRadius: 8,
  border: "1px solid #ddd",
};

const emptyPreview = {
  width: "100%",
  height: "60vh",
  border: "1px solid #ddd",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#777",
  borderRadius: 8,
};

const infoBox = {
  background: "#fff",
  borderRadius: 12,
  padding: 16,
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
};

const versionsBox = {
  background: "#fff",
  borderRadius: 12,
  padding: 16,
  maxHeight: "70vh",
  overflowY: "auto",
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
};

const versionCard = {
  border: "1px solid #ddd",
  borderRadius: 10,
  padding: 10,
  marginBottom: 10,
};

const smallRow = {
  margin: "2px 0",
  fontSize: 13,
};

const versionBtns = {
  display: "flex",
  justifyContent: "flex-end",
  gap: 8,
  marginTop: 6,
};

const viewBtn = {
  padding: "5px 10px",
  borderRadius: 6,
  background: "#6c757d",
  border: "none",
  color: "#fff",
};

const approveBtn = {
  padding: "5px 10px",
  borderRadius: 6,
  background: "#0d6efd",
  border: "none",
  color: "#fff",
};

const pdfBtn = {
  padding: "5px 10px",
  borderRadius: 6,
  background: "#198754",
  border: "none",
  color: "#fff",
};

const editVersionBtn = {
  padding: "8px 14px",
  borderRadius: 8,
  background: "#198754",
  color: "#fff",
  border: "none",
  cursor: "pointer",
  marginBottom: 10,
};

const unapproveBtn = {
  padding: "8px 14px",
  borderRadius: 8,
  background: "#c62828",
  color: "#fff",
  border: "none",
  cursor: "pointer",
  marginBottom: 10,
};

const grayBtn = {
  padding: "8px 14px",
  borderRadius: 8,
  background: "#6c757d",
  color: "#fff",
  border: "none",
};

const backBtn = {
  padding: "10px 18px",
  borderRadius: 8,
  background: "#0d6efd",
  color: "#fff",
  border: "none",
};

const infoRow = {
  margin: "4px 0",
  fontSize: 14,
  textAlign: "right",
};

export default InvoiceViewPage;
